import * as TabsPrimitive from '@radix-ui/react-tabs';
import { Copy, Download, ImageIcon, RefreshCw } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CredentialPngRenderer,
  SOCIAL_IMAGE_HEIGHT,
  SOCIAL_IMAGE_WIDTH,
} from './CredentialPngRenderer';
import type { ExportArtItem, ShareConfig } from './types';

type Network = 'x' | 'linkedin';

function shareText(
  selectedWord: 'seleccionado' | 'seleccionada',
  share: ShareConfig,
) {
  return `Fui ${selectedWord} entre cientos de hackers para Platanus Hack 26: ${share.city}.

Estaré ${share.dates} (36 horas!) junto a 100+ hackers top del país creando un producto desde cero.

Se viene 🔥`;
}

function shareUrl(network: Network, text: string, shareUrlBase: string) {
  const encodedText = encodeURIComponent(text);
  const url = encodeURIComponent(shareUrlBase);
  return network === 'x'
    ? `https://twitter.com/intent/tweet?text=${encodedText}&url=${url}`
    : `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
}

function XIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}

function LinkedInIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M20.452 20.452h-3.554v-5.569c0-1.328-.025-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.356V9h3.414v1.561h.046c.477-.9 1.637-1.852 3.37-1.852 3.602 0 4.267 2.37 4.267 5.455v6.288ZM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124Zm1.778 13.019H3.555V9h3.56v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z" />
    </svg>
  );
}

export function ShareButton({
  face,
  handle,
  selectedWord,
  share,
  exportArt,
}: {
  face: ReactNode;
  handle: string;
  selectedWord: 'seleccionado' | 'seleccionada';
  share: ShareConfig;
  exportArt?: ExportArtItem[];
}) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [network, setNetwork] = useState<Network>('x');
  const [previewUrl, setPreviewUrl] = useState('');
  const [exportRequestId, setExportRequestId] = useState(0);
  const [exportError, setExportError] = useState('');
  const [copied, setCopied] = useState(false);
  const currentShareText = shareText(selectedWord, share);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (open && !previewUrl && exportRequestId === 0) {
      setExportRequestId(1);
    }
  }, [exportRequestId, open, previewUrl]);

  function handleShare() {
    window.open(
      shareUrl(network, currentShareText, share.url),
      '_blank',
      'noopener,noreferrer,width=600,height=600',
    );
    setOpen(false);
  }

  function handleRegenerateImage() {
    setExportError('');
    setPreviewUrl('');
    setExportRequestId((current) => current + 1);
  }

  async function handleCopyText() {
    await navigator.clipboard.writeText(`${currentShareText}\n\n${share.url}`);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <>
      <div
        className="absolute right-0 bottom-[6%] left-0 z-40 flex justify-center"
        style={{
          opacity: mounted ? 1 : 0,
          transform: `translateY(${mounted ? 0 : 20}px)`,
          transition:
            'opacity 700ms ease-out 600ms, transform 900ms cubic-bezier(0.22, 1, 0.36, 1) 600ms',
        }}
      >
        {/* The pulse keyframes were styled-jsx over there; vite has no equivalent, so they
            live in stylesheets/credential.css (PORTING.md U7). */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="credential-share-pulse font-aeonik-mono-bold flex items-center gap-3 border border-white/20 bg-black/70 px-6 py-3 text-sm text-white tracking-[0.32em] backdrop-blur-md transition-colors hover:bg-primary hover:text-primary-foreground"
        >
          <span>COMPARTIR</span>
          <span className="flex items-center gap-2 opacity-70">
            <XIcon className="size-4" />
            <LinkedInIcon className="size-4" />
          </span>
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-aeonik-mono-bold">
              COMPARTIR CREDENCIAL
            </DialogTitle>
            <DialogDescription>
              Imagen vertical lista para descargar y compartir tu credencial PH26
              en redes sociales.
            </DialogDescription>
          </DialogHeader>

          <TabsPrimitive.Root
            value={network}
            onValueChange={(v) => setNetwork(v as Network)}
            className="w-full"
          >
            <TabsPrimitive.List className="grid w-full grid-cols-2 gap-1 bg-muted p-1">
              <TabsPrimitive.Trigger
                value="x"
                className="flex items-center justify-center gap-2 px-3 py-2 font-medium text-sm transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <XIcon className="size-4" />X
              </TabsPrimitive.Trigger>
              <TabsPrimitive.Trigger
                value="linkedin"
                className="flex items-center justify-center gap-2 px-3 py-2 font-medium text-sm transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <LinkedInIcon className="size-4" />
                LinkedIn
              </TabsPrimitive.Trigger>
            </TabsPrimitive.List>
          </TabsPrimitive.Root>

          <div className="overflow-hidden rounded-md border bg-black">
            <div
              className="relative w-full"
              style={{
                aspectRatio: `${SOCIAL_IMAGE_WIDTH} / ${SOCIAL_IMAGE_HEIGHT}`,
              }}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Preview de la credencial para redes sociales"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-3 bg-black text-white/70">
                  <ImageIcon className="size-8 text-primary" />
                  <span className="text-center text-sm">
                    {exportError || 'Generando preview 3D...'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleRegenerateImage}
              disabled={!previewUrl && !exportError}
            >
              <RefreshCw className="size-4" />
              Regenerar
            </Button>
            <Button type="button" disabled={!previewUrl} asChild>
              <a
                href={previewUrl || undefined}
                download={`platanus-hack-26-${handle}.png`}
              >
                <Download className="size-4" />
                Descargar PNG
              </a>
            </Button>
          </div>

          <div className="whitespace-pre-line rounded-md border bg-muted/30 p-4 text-muted-foreground text-sm">
            {currentShareText}
            <br />
            <span className="text-foreground">{share.url}</span>
          </div>

          <Button type="button" variant="outline" onClick={handleCopyText}>
            <Copy className="size-4" />
            {copied ? 'Texto copiado' : 'Copiar texto'}
          </Button>

          <Button onClick={handleShare} className="w-full">
            Abrir {network === 'x' ? 'X' : 'LinkedIn'}
          </Button>
        </DialogContent>
      </Dialog>

      <CredentialPngRenderer
        face={face}
        art={exportArt}
        requestId={exportRequestId}
        onCapture={(image) => {
          setPreviewUrl(image);
          setExportError('');
        }}
        onError={setExportError}
      />
    </>
  );
}
